import React, { useState } from 'react'
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js/min'
import './phoneField.scss'

const names = new Intl.DisplayNames(['en'], { type: 'region' })
const countries = getCountries().map(country => ({ country, code: getCountryCallingCode(country), name: names.of(country) }))
  .sort((a, b) => a.name.localeCompare(b.name))

const splitPhone = (value, defaultCountry) => {
  const international = value.trim().replace(/^00/, '+')
  if (!international.startsWith('+')) return { country: value ? '' : defaultCountry, number: value }
  const parsed = parsePhoneNumberFromString(international, { extract: false })
  const matches = countries.filter(item => international.startsWith('+' + item.code))
  const match = matches.find(item => item.country === parsed?.country)
    || matches.find(item => item.country === defaultCountry) || (matches.length === 1 ? matches[0] : null)
  return match ? { country: match.country, number: international.slice(match.code.length + 1).trimStart() } : { country: '', number: value }
}

const joinPhone = (number, country) => {
  if (!number.trim()) return ''
  if (!country) return number
  // Interpret familiar numbers when possible; unusual/legacy text never blocks saving.
  const parsed = parsePhoneNumberFromString(number, { defaultCountry: country, extract: false })
  return parsed?.isPossible() ? parsed.number + (parsed.ext ? ' ext. ' + parsed.ext : '') : '+' + getCountryCallingCode(country) + ' ' + number
}

export default function PhoneField({ id, name, label, value = '', onChange, onBlur, error, disabled = false, defaultCountry = 'RO' }) {
  const [draft, setDraft] = useState(() => ({ source: value, ...splitPhone(value, defaultCountry) }))
  // External updates (lead suggestions, copy owner details, reload) replace the draft.
  if (value !== draft.source) setDraft({ source: value, ...splitPhone(value, defaultCountry) })
  const update = (number, country) => {
    const source = joinPhone(number, country)
    setDraft({ source, number, country }); onChange(source)
  }
  return <div className="phone-field form-group mb-0">
    <label htmlFor={id}>{label}</label>
    <div className="phone-entry">
      <select id={id + '-prefix'} className="form-control" value={draft.country} disabled={disabled}
        aria-label={label.replace(' (optional)', '') + ' country code'} onChange={event => update(draft.number, event.target.value)}>
        <option value="">No added prefix</option>
        {countries.map(item => <option key={item.country} value={item.country}>{item.name} ({item.country} +{item.code})</option>)}
      </select>
      <input id={id} name={name} type="tel" autoComplete="tel-national" className="form-control" disabled={disabled}
        value={draft.number} onBlur={onBlur} aria-invalid={Boolean(error)} aria-describedby={id + '-help'}
        onChange={event => {
          const number = event.target.value
          const next = /^\s*(?:\+|00)/.test(number) ? splitPhone(number, draft.country) : { number, country: draft.country }
          update(next.number, next.country)
        }} />
    </div>
    <small id={id + '-help'} className={error ? 'text-danger' : 'text-muted'}>{error || 'Choose a prefix and enter the number, or paste the full number.'}</small>
  </div>
}
